import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Link,
  Avatar,
  Text,
  Flex,
  Tooltip,
  Button,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  FormLabel,
  Select,
  ModalFooter,
  useColorModeValue,
  Divider,
  StackDivider,
  Icon,
  Input
} from "@chakra-ui/react";
import {
  CheckIcon,
  ExternalLinkIcon,
  PhoneIcon,
  ArrowBackIcon,
  InfoIcon,
  CloseIcon,
  AtSignIcon
} from "@chakra-ui/icons";
import { RiSparkling2Fill } from "react-icons/ri";
import { FaRedo, FaCreditCard, FaLightbulb, FaCalendarTimes } from "react-icons/fa";
import { BsThreeDotsVertical } from "react-icons/bs";
import { getBid, cqAPI, Auth } from "../../lib/Auth";
import creditCardModalManager from "../DialogManager/creditCardModalManager";
import { useNavigate } from "react-router-dom";
import EditableText from "../EditableText/EditableText";
import HealthScoreBar from "./HealthScoreBar";
import { emailValidator, phoneValidator, fullNameValidator, dobValidator } from "../../lib/Validator";
import { useAppContext } from "../../AppContext";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faUser} from "@fortawesome/free-solid-svg-icons";

function UserInfo({
  setConversationClosed,
  userInfo,
  uid,
  conversationClosed,
  messages,
  toast,
  setUserInfo,
  isStripeConnected,
  addActionBarItem,
  relatedUid,
  removeActionBarItem, // Add these props
  isMobile,
  assigned,
  setAssigned
}) {
  const bid = getBid();
  const isAva = uid === "000000000000000000000000";
  const path = window.location.pathname.split('/')[1];
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [extension, setExtension] = useState((Auth.user.default_extension || {})[bid]);
  const [selectedPhone, setSelectedPhone] = useState(userInfo.phone || "");
  const [extensions, setExtensions] = useState([]);
  const userInfoTopBoxBG = useColorModeValue('#D8DEDF', '#303343');
  const dividerColor = useColorModeValue('#E5E5EF', '#64728C');
  const [showUserInfo, setShowUserInfo] = useState(!isMobile)
  const { isAppInTestDebugMode } = useAppContext();

  // ---- NEW: AI Pause/Resume modal state ----
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [aiAction, setAiAction] = useState("pause"); // "pause" | "resume"
  const [pauseOption, setPauseOption] = useState("1_hour");
  const [customDateTime, setCustomDateTime] = useState("");
  const [aiSubmitting, setAiSubmitting] = useState(false);

  // ---- SS-4080: per-patient "Do not schedule" flag (per business, like Pause AI) ----
  const [doNotScheduleSubmitting, setDoNotScheduleSubmitting] = useState(false);

  const isDoNotSchedule = () => !!userInfo?.do_not_schedule?.enabled;

  // ---- SS-4080: toggle the patient's "Do not schedule" flag for this business ----
  const toggleDoNotSchedule = async () => {
    const enabled = !isDoNotSchedule();
    try {
      setDoNotScheduleSubmitting(true);
      const res = await cqAPI.post(`/do-not-schedule?_uid=${userInfo.user_id}`, { enabled });
      if (res.type === "ok" && res.data?.do_not_schedule) {
        // Functional update, and only for the contact this request was made for:
        // a websocket profile update (or navigating to another patient) can land
        // while the POST is in flight, and spreading the captured userInfo would
        // discard it — or paste this patient's flag onto the next one.
        setUserInfo((current) =>
          String(current?.user_id) === String(userInfo.user_id)
            ? { ...current, do_not_schedule: res.data.do_not_schedule }
            : current
        );
        toast({
          title: res.data.do_not_schedule.enabled ? "Do Not Schedule enabled" : "Do Not Schedule removed",
          description: res.data.do_not_schedule.enabled
            ? "The AI will not book or reschedule for this patient, and will leave a task for the office."
            : "The AI can schedule for this patient again.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      throw new Error(res.msg || "Unable to update Do Not Schedule.");
    } catch (e) {
      toast({
        title: "Error",
        description: "Unable to update the Do Not Schedule setting.",
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setDoNotScheduleSubmitting(false);
    }
  };

  const isAiPaused = () => {
    if (!userInfo?.ai_response) return false;
    const { disabled, expires } = userInfo.ai_response;
    if (!disabled) return false;
    if (expires === -1) return true;
    if (!expires) return false;
    const exp = typeof expires === "string" ? new Date(expires) : expires;
    return exp.getTime() > Date.now();
  };
  
  const isTask = window.location.pathname.startsWith("/task")

  useEffect(() => {
    setShowUserInfo(!isMobile);
  }, [isMobile])

  useEffect(() => {
    console.log('path is', path);
    const itemsToAdd = [
      {
        id: "avatar",
        component: (
          relatedUid? <Box position="relative" style={{ marginLeft: "40px" }}>
            <Link href={`/?bid=${bid}&_uid=${relatedUid}`} target="_blank">
              <Avatar size="sm" src={userInfo.pic} />
              <IconButton
                icon={<ExternalLinkIcon />}
                variant="none"
                position="absolute"
                top="0"
                right="0"
                transform="translate(50%, -50%)"
              />
            </Link>
          </Box> : <></>
        ),
        align: "left",
      },
      {
        id: "profile-name",
        component: (
          <Text className="profile-name" style={{ fontWeight: "600" }}>
            {isAva? "Autopilot" : userInfo?.name}
          </Text>
        ),
        align: "left",
      },
      {
        id: "conversation-status",
        component: (
          <IconButton
            icon={conversationClosed ? <FaRedo/> : <CheckIcon/>}
            size="sm"
            variant="ghost"
            onClick={toggleConversationStatus}
            _hover={{ bg: "transparent" }}
            _active={{ bg: "transparent" }}
            _focus={{ boxShadow: "none" }}
          />
        ),
        align: "right",
      },
      {
        id: "more-options",
        component: (
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<BsThreeDotsVertical />}
              variant="ghost"
              _hover={{ bg: "transparent" }}
              _active={{ bg: "transparent" }}
              _focus={{ boxShadow: "none" }}
            />
            <MenuList>
              <MenuItem
                icon={<PhoneIcon />}
                isDisabled={!userInfo.phone}
                onClick={() => handleCallPatient()}
              >
                Call Patient
              </MenuItem>
              {isTask && userInfo.user_id && userInfo.email && (
                <MenuItem
                  icon={<FontAwesomeIcon icon={faUser} />}
                  isDisabled={!userInfo.user_id}
                  onClick={() => window.open(`/inbox/${userInfo.user_id}?bid=${bid}`, '_blank')}
                >
                  Contact Patient
                </MenuItem>
              )}
              <MenuItem
                icon={<FaLightbulb />}
                isDisabled={!userInfo?.patient_guid}
                onClick={() =>
                  navigate(`/patient/${userInfo.patient_guid}?bid=${bid}`)
                }
              >
                Patient Insight
              </MenuItem>
              {isStripeConnected && window.location.pathname.startsWith("/inbox") && (<MenuItem
                isDisabled={!relatedUid}
                icon={<Box display="flex" alignItems="center">
                  <Text mr={2}>{userInfo?.source?.last4}</Text>
                  <FaCreditCard/>
                </Box>}
                onClick={() =>
                  creditCardModalManager.open({
                    hideInstallment: true,
                    userProfileName: userInfo.name,
                    initialSource: {
                      type: userInfo?.source?.type,
                      last4: userInfo?.source?.last4,
                    },
                    _uid: uid,
                    onCardChange: ({ type, last4 }) =>
                      setUserInfo({
                        ...userInfo,
                        source: {
                          type,
                          last4,
                        },
                      }),
                    isTestMode: isAppInTestDebugMode,
                  })
                }
              >
                Run A Payment
              </MenuItem>)}

              {userInfo.ai_response && !isTask && (<MenuItem
                icon={
                    <Icon as={RiSparkling2Fill} strokeWidth="2" color={isAiPaused() ? "red.500" : undefined} /> 
                  }
                size="sm"
                variant="userInfoButton"
                onClick={pauseAIResponse}
                color={isAiPaused() ? "red.500" : undefined}
                isDisabled={isAva}
              >
                {isAiPaused() ? "AI Response Paused" : "Pause AI Response"}
              </MenuItem>)}

              {userInfo.do_not_schedule && !isTask && (<MenuItem
                icon={<FaCalendarTimes color={isDoNotSchedule() ? "var(--chakra-colors-red-500)" : undefined} />}
                size="sm"
                variant="userInfoButton"
                onClick={toggleDoNotSchedule}
                color={isDoNotSchedule() ? "red.500" : undefined}
                isDisabled={isAva || doNotScheduleSubmitting}
              >
                {isDoNotSchedule() ? "Do Not Schedule" : "Mark Do Not Schedule"}
              </MenuItem>)}

              <MenuItem
                isDisabled={assigned || conversationClosed || !relatedUid}
                icon={<AtSignIcon/>}
                size="sm"
                variant="userInfoButton"
                onClick={takeover}
              >
                {assigned ? `Assigned to ${assigned}` : "Assign to Yourself"}
              </MenuItem>

              <MenuItem
                icon={<InfoIcon />}
                onClick={toggleShowInfo}
              >
                Info
              </MenuItem>
            </MenuList>
          </Menu>
        ),
        align: "right",
      },
    ];

    setSelectedPhone(userInfo.phone)
    itemsToAdd.forEach(addActionBarItem);

    return () => {
      itemsToAdd.forEach((item) => removeActionBarItem(item.id));
    };
    // doNotScheduleSubmitting: the action-bar menu item closes over it, so without
    // it here a re-registration that happened mid-request (a userInfo update from
    // the websocket) would leave the item disabled after the request finished.
  }, [userInfo, assigned, conversationClosed, doNotScheduleSubmitting]);

  const toggleShowInfo = () => {
    setShowUserInfo(prevState => !prevState)
  };

  const onClose = () => {
    if (path === 'inbox') {
      navigate(`/inbox?bid=${bid}`);
      return;
    }
    navigate(`/tasks?bid=${bid}`);
  };

  const toggleConversationStatus = async () => {
    const lastMessage = messages.findLast((x) => x._id && !x.call_id && String(x.bid) === String(bid));
    if (!lastMessage){
      return
    }
    const response = await cqAPI.put("/messages", {
      closed: !conversationClosed,
      _id: lastMessage._id.split(':')[0],
    });
    if (response.type === "ok") {
      setConversationClosed(!conversationClosed);
    }
  };

  async function updateProfile(field, value) {
    try {
      const response = await cqAPI.post(`/profile?_uid=${uid}`, {
        [field]: value
      });

      if (response.type === 'ok') {
        if (response.msg){
          toast({
            title: "Success",
            description: response.msg,
            status: "success",
            duration: 9000,
            isClosable: true,
          });
          return
        }
        setUserInfo({ ...userInfo, [field]: value });
        return true;
      }
      toast({
        title: "Error",
        description: response.msg,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return false;
    } catch (error) {
      console.error("Error updating profile: ", error);
    }
  }

  const handleCallPatient = () => {
    setIsModalOpen(true);
    setShowUserInfo(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleExtensionChange = async (e) => {
    const newExtension = e.target.value;
    const response = await cqAPI.post(`/extensions`, {
      extension_id: newExtension,
    });
    if (response.msg) {
      toast({
        title: "Error",
        description: response.msg,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
    if (response.type === "ok") {
      const default_extension = Auth.user.default_extension || {};
      default_extension[bid] = newExtension;
      Auth.storeUser({ ...Auth.user, default_extension });
      setExtension(newExtension);
    }
  };

  const handlePhoneChange = (e) => {
    setSelectedPhone(e.target.value);
  };
  
  // ---- NEW: Pause/Resume AI Response flow ----
  const pauseAIResponse = async ()=>{
    // Open modal for pause or resume based on current state
    if (isAiPaused()) {
      setAiAction("resume");
    } else {
      setAiAction("pause");
    }
    setPauseOption("1_hour");
    setCustomDateTime("");
    setIsAiModalOpen(true);
  };

  const handleAiConfirm = async () => {
    try {
      setAiSubmitting(true);
      if (aiAction === "resume") {
        const res = await cqAPI.post(`/ai-response?_uid=${userInfo.user_id}`, { disabled: false });
        if (res.type === "ok" && res.data?.ai_response) {
          setUserInfo({ ...userInfo, ai_response: res.data.ai_response });
        }
        toast({
          title: "AI Response Resumed",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        let expiresToSend = null;
        if (pauseOption === "custom") {
          if (!customDateTime) {
            toast({
              title: "Select a date & time",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            setAiSubmitting(false);
            return;
          }
          expiresToSend = moment(customDateTime).toISOString();
        } else if (pauseOption === "forever") {
          expiresToSend = -1;
        } else {
          const now = moment();
          switch (pauseOption) {
            case "1_hour": expiresToSend = now.add(1, "hour").toISOString(); break;
            case "3_hours": expiresToSend = now.add(3, "hour").toISOString(); break;
            case "6_hours": expiresToSend = now.add(6, "hour").toISOString(); break;
            case "12_hours": expiresToSend = now.add(12, "hour").toISOString(); break;
            case "1_day": expiresToSend = now.add(1, "day").toISOString(); break;
            case "3_days": expiresToSend = now.add(3, "day").toISOString(); break;
            case "1_week": expiresToSend = now.add(1, "week").toISOString(); break;
            default: expiresToSend = now.add(1, "hour").toISOString();
          }
        }
        const res = await cqAPI.post(`/ai-response?_uid=${userInfo.user_id}`, { disabled: true, expires: expiresToSend });
        if (res.type === "ok" && res.data?.ai_response) {
          setUserInfo({ ...userInfo, ai_response: res.data.ai_response });
        }
        toast({
          title: "AI Response Paused",
          description: expiresToSend === -1 ? "Paused indefinitely." : `Until ${moment(expiresToSend).format("MMM D, YYYY h:mm A")}`,
          status: "success",
          duration: 7000,
          isClosable: true,
        });
      }
    } catch (e) {
      toast({
        title: "Error",
        description: "Unable to update AI response status.",
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setAiSubmitting(false);
      setIsAiModalOpen(false);
    }
  };

  const takeover = async ()=>{
    const response = await cqAPI.post('/takeover',{_id : uid});
    if (response.type === 'ok'){
      setAssigned("You")
    }
  }

  const handleCallNow = async () => {
    if (!extension) {
      toast({
        title: "Error",
        description: "Please select your extension.",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return;
    }
    const response = await cqAPI.post(`/dial`, {
      phone: selectedPhone,
      uid,
    });
    if (response.type === "error" && response.msg) {
      toast({
        title: "Error",
        description: response.msg,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return;
    }
    toast({
      title: "Success",
      description: `Calling extension ${extensions.find(
        (e) => e._id === extension
      )?.numeric_value}.`,
      status: "success",
      duration: 9000,
      isClosable: true,
    });
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (isModalOpen) {
      setShowUserInfo(true);
      const fetchExtensions = async () => {
        try {
          const response = await cqAPI.get("/extensions");
          if (response.type === "ok") {
            setExtensions(response.data);
            const default_extension = Auth.user.default_extension || {};
            if (default_extension[bid] && !extensions.includes(default_extension[bid])){
              default_extension[bid] = null;
              Auth.storeUser({ ...Auth.user, default_extension });
            }
          }
        } catch (error) {
          console.error("Error fetching extensions: ", error);
        }
      };

      fetchExtensions();
    }
  }, [isModalOpen]);

  const handleDOBChange = async (date) => {
    if (!date){
      return
    }
    const m = moment(date, "MM/DD/YYYY", true);
    if (m.isValid()) {
     return await updateProfile("dob", m.toISOString())
    }
  }

  const getDOB = () => {
    if (!userInfo.dob) {
      return null;
    }
    const m = moment(userInfo.dob, "YYYY-MM-DD");
    if (m.isValid()) {
      return m.format("MM/DD/YYYY");
    }
    return null;
  };


  const formatHealthBarData = (data) => {
    const healthScore = data.healthScoreData?.length > 0 ? data.healthScoreData[0]?.value : 0;
    return [
        { name: "Value", value: healthScore },
        { name: "Remaining", value: 100 - healthScore },
      ]
  };

  return (
    showUserInfo && (
      <Flex className={`user-info collapsed right-menu`} position="relative" height="100%">
        <Box bg={userInfoTopBoxBG} p={1} className="top-box" direction="column">
          <Button variant="backButtonInbox" leftIcon={<ArrowBackIcon/>} onClick={onClose}>
            {`Back to ${path}`}
          </Button>
          <Flex justify="flex-start" alignItems="top">
            {relatedUid && <Avatar size="lg" src={userInfo.pic} ml={2} mr={4} mt={3}/>}
            <VStack spacing={1} pl={1} alignItems="left" width="70%">
              <Flex align="center" className="name-info">
                <EditableText
                  initialValue={isAva ? "Autopilot" : userInfo.name}
                  onSave={(e) => updateProfile("name", e)}
                  showPlaceholderInDisplayMode={false}
                  placeholder="Name"
                  pattern={fullNameValidator.pattern}
                  invalid={fullNameValidator.invalid}
                  editable={relatedUid}
                />
              </Flex>
              {userInfo?.discount_plan?.name && (
                <Flex><Text>{userInfo.discount_plan.name} - {userInfo.discount_plan.status}</Text></Flex>)}
              {relatedUid && <>
                <Flex align="center" className="email-info">
                  <EditableText
                    initialValue={userInfo.email}
                    showValidationToast={false}
                    onSave={(e) => updateProfile("email", e)}
                    showPlaceholderInDisplayMode={true}
                    pattern={emailValidator.pattern}
                    invalid={emailValidator.invalid}
                    placeholder="No Email"/>
                </Flex>
                <Flex align="center" className="phone-info">
                  <EditableText
                    initialValue={userInfo.phone}
                    onSave={(e) => updateProfile("phone", e)}
                    showPlaceholderInDisplayMode={true}
                    placeholder="Cell Phone Number"
                    pattern={phoneValidator.pattern}
                    invalid={phoneValidator.invalid}
                  />
                </Flex>
                <Flex align="center" className="dob">
                  <EditableText
                    initialValue={getDOB()}
                    onSave={(e) => handleDOBChange(e)}
                    showPlaceholderInDisplayMode={true}
                    placeholder="Date of Birth"
                    pattern={dobValidator.pattern}
                    invalid={dobValidator.invalid}
                  />
                </Flex>
                <Link id="go-to-patient" textAlign="left" href={`/?bid=${bid}&_uid=${relatedUid}`} target="_blank" fontSize="14px"
                      color="#3182ce" fontWeight="600">
                  Go to Patient
                </Link>
              </>}
            </VStack>
          </Flex>
        </Box>

        {relatedUid && <Flex
          height="150px"
          width="300px"
          alignSelf="center"
          justify="center"
          marginTop="-15px"
          marginLeft="2px"
          position="relative"
          filter={!userInfo.patient_guid ? 'grayscale(100%)' : 'none'}
        >
          <HealthScoreBar
            data={formatHealthBarData(userInfo)}
          />
        </Flex>}

        {!isMobile && (
          <Flex
            width="100%"
            paddingTop="20px"
            justifyContent="flex-start"
            wrap="wrap"
            gap={2}
          >
            <Divider ml={2} mr={2} borderColor={dividerColor}/>

            <VStack
              spacing={2}
              justifyContent="center"
              alignItems="left"
              width="100%"
              pl={2}
              pr={2}
              divider={<StackDivider borderColor={dividerColor}/>}
            >
              {isStripeConnected && window.location.pathname.startsWith("/inbox") && ( //change back to off
                <Button
                  isDisabled={!relatedUid}
                  leftIcon={<Box>
                    <Text mr={2}>{userInfo?.source?.last4}</Text>
                    <FaCreditCard/>
                  </Box>}
                  size="sm"
                  variant="userInfoButton"
                  onClick={() =>
                    creditCardModalManager.open({
                      hideInstallment: true,
                      userProfileName: userInfo.name,
                      initialSource: {
                        type: userInfo?.source?.type,
                        last4: userInfo?.source?.last4,
                      },
                      _uid: uid,
                      onCardChange: ({ type, last4 }) =>
                        setUserInfo({
                          ...userInfo,
                          source: {
                            type,
                            last4,
                          },
                        }),
                      isTestMode: isAppInTestDebugMode,
                    })
                  }
                >
                  Run A Payment
                </Button>
              )}
              {userInfo.phone ? (
                <Button
                  leftIcon={<PhoneIcon/>}
                  size="sm"
                  variant="userInfoButton"
                  onClick={handleCallPatient}
                >
                  Call Patient
                </Button>
              ) : (
                <Tooltip label="Patient has no phone number." aria-label="A tooltip">
                  <Button
                    leftIcon={<PhoneIcon/>}
                    size="sm"
                    variant="userInfoButton"
                    isDisabled={true}
                  >
                    Call Patient
                  </Button>
                </Tooltip>
              )}
              {path === 'tasks' && userInfo.user_id && userInfo.email && (
                <Button
                  leftIcon={<FontAwesomeIcon icon={faUser} />}
                  size="sm"
                  variant="userInfoButton"
                  isDisabled={!userInfo}
                  onClick={() => window.open(`/inbox/${userInfo.user_id}?bid=${bid}`, '_blank')}
                >
                  Contact Patient
                </Button>
              )}
              {userInfo.patient_guid ? (
                <Button
                  leftIcon={<FaLightbulb/>}
                  size="sm"
                  variant="userInfoButton"
                  onClick={() => {
                    navigate(`/patient/${userInfo.patient_guid}?bid=${bid}`);
                  }}
                >
                  Patient Insight
                </Button>
              ) : (
                <Tooltip label="Not enough information about this patient." aria-label="A tooltip">
                  <Button
                    leftIcon={<FaLightbulb/>}
                    isDisabled={true}
                    size="sm"
                    variant="userInfoButton"
                  >
                    Patient Insight
                  </Button>
                </Tooltip>
              )}
              {userInfo.ai_response && !isTask && (<Button
                leftIcon={
                    <Icon as={RiSparkling2Fill} strokeWidth="2" color={isAiPaused() ? "red.500" : undefined} /> 
                  }
                size="sm"
                variant="userInfoButton"
                onClick={pauseAIResponse}
                color={isAiPaused() ? "red.500" : undefined}
                isDisabled={isAva}
              >
                {isAiPaused() ? "AI Response Paused" : "Pause AI Response"}
              </Button>)}
              {userInfo.do_not_schedule && !isTask && (<Button
                leftIcon={<FaCalendarTimes color={isDoNotSchedule() ? "var(--chakra-colors-red-500)" : undefined} />}
                size="sm"
                variant="userInfoButton"
                onClick={toggleDoNotSchedule}
                color={isDoNotSchedule() ? "red.500" : undefined}
                isDisabled={isAva || doNotScheduleSubmitting}
              >
                {isDoNotSchedule() ? "Do Not Schedule" : "Mark Do Not Schedule"}
              </Button>)}
              <Button
                isDisabled={assigned || conversationClosed || !relatedUid}
                leftIcon={<AtSignIcon/>}
                size="sm"
                variant="userInfoButton"
                onClick={takeover}
              >
                {assigned ? `Assigned to ${assigned}` : "Assign to Yourself"}
              </Button>

            </VStack>
            <Divider ml={2} mr={2} borderColor={dividerColor}/>
          </Flex>
        )}


        <Flex justifyContent="right" position="absolute" bottom="4" right="4" width="100%">

          { (
            !isMobile ? (<Button
                id={"convo-status-btn"}
                leftIcon={conversationClosed ? <FaRedo/> : <CheckIcon/>}
                size="sm"
                variant="solid"
                onClick={toggleConversationStatus}
              >
                {(conversationClosed ? "Open " : "Close ") +
                  (path === 'inbox' ? "Conversation" : 'Task')
                }
              </Button>)
              : (
                <Button
                  leftIcon={<CloseIcon/>}
                  size="sm"
                  variant="solid"
                  onClick={toggleShowInfo}
                >
                  Close Info
                </Button>
              ))}
        </Flex>

        {/* Existing Call Patient Modal */}
        <Modal isOpen={isModalOpen} onClose={handleModalClose}>
          <ModalOverlay/>
          <ModalContent>
            <ModalHeader>Call Patient</ModalHeader>
            <ModalCloseButton/>
            <ModalBody>
              <FormControl>
                <FormLabel>Extension</FormLabel>
                <Select value={extension || ''} onChange={handleExtensionChange}>
                  <option value="" disabled>
                    -- Select an extension --
                  </option>
                  {extensions.map((ext) => (
                    <option key={ext._id} value={ext._id}>
                      {ext.numeric_value}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Phone Number</FormLabel>
                <Select value={selectedPhone} onChange={handlePhoneChange}>
                  {userInfo?.phones?.map((phone) => (
                    <option key={phone} value={phone}>
                      {phone}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={handleCallNow}>
                Call Now
              </Button>
              <Button variant="ghost" onClick={handleModalClose}>
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* NEW: Pause/Resume AI Response Modal */}
        <Modal isOpen={isAiModalOpen} onClose={() => setIsAiModalOpen(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{aiAction === "resume" ? "Resume AI Response?" : "Pause AI Response?"}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {aiAction === "resume" ? (
                <>
                  <Text mb={3}>
                    {(() => {
                      const { expires } = userInfo.ai_response || {};
                      if (expires === -1) return "AI response is paused forever.";
                      if (expires) {
                        const expStr = moment(expires).format("MMM D, YYYY h:mm A");
                        return `AI response is paused until ${expStr}.`;
                      }
                      return "AI response is currently paused.";
                    })()}
                  </Text>
                  <Text>Resume now?</Text>
                </>
              ) : (
                <>
                  <FormControl>
                    <FormLabel>Pause for</FormLabel>
                    <Select value={pauseOption} onChange={(e) => setPauseOption(e.target.value)}>
                      <option value="1_hour">1 hour</option>
                      <option value="3_hours">3 hours</option>
                      <option value="6_hours">6 hours</option>
                      <option value="12_hours">12 hours</option>
                      <option value="1_day">1 day</option>
                      <option value="3_days">3 days</option>
                      <option value="1_week">1 week</option>
                      <option value="forever">Forever</option>
                      <option value="custom">Custom...</option>
                    </Select>
                  </FormControl>
                  {pauseOption === "custom" && (
                    <FormControl mt={4}>
                      <FormLabel>Resume on</FormLabel>
                      <Input
                        type="datetime-local"
                        value={customDateTime}
                        onChange={(e) => setCustomDateTime(e.target.value)}
                      />
                    </FormControl>
                  )}
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme={aiAction === "resume" ? "blue" : "red"}
                mr={3}
                onClick={handleAiConfirm}
                isLoading={aiSubmitting}
              >
                {aiAction === "resume" ? "Resume" : "Confirm"}
              </Button>
              <Button variant="ghost" onClick={() => setIsAiModalOpen(false)}>
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex>
    )
  )
}

export default UserInfo;
